import './MonthlyTicks.scss';
import { getDaysInMonth } from 'date-fns';
import { ReactElement } from 'react';
import {
  convertDateToUTC,
  getFormattedMonthName
} from '@blisspointmedia/bpm-types/dist/formatUtils';

interface RechartsTickProps {
  textAnchor: string;
  verticalAnchor: string;
  orientation: string;
  width: number;
  height: number;
  x: number;
  y: number;
  className: string;
  stroke: string;
  fill: string;
  index: number;
  payload: {
    coordinate: number;
    value: any; // Value expected to be a numeric timestamp
    offset: number;
  };
  visibleTicksCount: number;
}

interface MonthlyTicksProps {
  rechartsProps: RechartsTickProps;
  yAxisWidth: number;
  numTotalDates: number;
}

const LINE_GREY = '#aaa';
const TEXT_GREY = '#666';
const Y_PADDING = 2;
// Size at which the longest text (ex. "March '24") overlaps into the next bracked over
const OVERFLOW_WIDTH = 54;
export const MonthlyTicks = ({
  rechartsProps,
  yAxisWidth,
  numTotalDates
}: MonthlyTicksProps): ReactElement<SVGElement> | null => {
  const { x, y, payload, width, visibleTicksCount, index } = rechartsProps;
  const { value } = payload;

  const dateAtTick = new Date(value);
  const daysInMonth = getDaysInMonth(convertDateToUTC(dateAtTick));
  const middleDateOfMonth = Math.ceil(daysInMonth / 2);

  let numDatesToDrawOver = 0;
  switch (dateAtTick.getUTCDate()) {
    case 1:
      numDatesToDrawOver = Math.floor(daysInMonth / 2);
      break;
    case daysInMonth:
      numDatesToDrawOver = Math.ceil(daysInMonth / 2) - 1;
      break;
    default:
      break;
  }

  const numPixelsPerDay = width / (numTotalDates - 1);
  const pathWidth = numPixelsPerDay * numDatesToDrawOver;
  const bracketWidth = daysInMonth * numPixelsPerDay;
  const monthCharLimit = bracketWidth < OVERFLOW_WIDTH ? 3 : 5;
  const monthFontSize = bracketWidth < OVERFLOW_WIDTH ? 10 : 12;

  // Vertical line drawn at first and last dates of the month
  const tickLine = (
    <path
      d={`M${x},${y + Y_PADDING}v${-8}`}
      stroke={LINE_GREY}
      className='recharts-cartesian-axis-tick-value monthly-ticks monthly-ticks-vertical'
    />
  );

  // Different path for first and last ticks so that they correctly stop at the edge of the axis
  let edgePath;
  if (index === 0) {
    const dist = yAxisWidth - x;
    edgePath = (
      <path
        d={`M${x},${y + Y_PADDING}h${dist}`}
        stroke={LINE_GREY}
        className='recharts-cartesian-axis-tick-value monthly-ticks monthly-ticks-first-of-graph'
      />
    );
  } else if (index === visibleTicksCount - 1) {
    const dist = width - x + yAxisWidth;
    edgePath = (
      <path
        d={`M${x},${y + Y_PADDING}h${dist}`}
        stroke={LINE_GREY}
        className='recharts-cartesian-axis-tick-value monthly-ticks monthly-ticks-last-of-graph'
      />
    );
  }

  switch (dateAtTick.getUTCDate()) {
    case 1: // Draw left half of bracket on first day of month
      return (
        <>
          {tickLine}
          {edgePath ?? (
            <path
              d={`M${x},${y + Y_PADDING}h${pathWidth}`}
              stroke={LINE_GREY}
              className='recharts-cartesian-axis-tick-value monthly-ticks monthly-ticks-first-of-month'
            />
          )}
        </>
      );
    case middleDateOfMonth: // Draw text on middle day of month
      return (
        <>
          {edgePath}
          <text
            x={x}
            y={y + 15}
            textAnchor='middle'
            color={TEXT_GREY}
            fontSize={monthFontSize}
            opacity={1}
            className='recharts-text recharts-cartesian-axis-tick-value'
            orientation='bottom'
          >
            {getFormattedMonthName(dateAtTick, monthCharLimit)}
          </text>
        </>
      );
    case daysInMonth: // Draw right half of bracket on last day of month
      return (
        <>
          {tickLine}
          {edgePath ?? (
            <path
              d={`M${x},${y + Y_PADDING}h${-pathWidth}`}
              stroke={LINE_GREY}
              className='recharts-cartesian-axis-tick-value monthly-ticks monthly-ticks-horizontal  monthly-ticks-last-of-month'
            />
          )}
        </>
      );
    default:
      return null;
  }
};
